import { useQuery } from '@tanstack/react-query';
import { Target, ClipboardList, ListChecks } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { StatCard } from '@/components/app/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiClient, humanizeError } from '@/lib/apiClient';
import { humanizePercent } from '@/lib/utils';

interface GoalPipeline {
  draft: number;
  active: number;
  graduated: number;
  total: number;
}

interface PhaseStats {
  statusCounts: {
    pending: number;
    active: number;
    evaluating: number;
    completed: number;
    total: number;
  };
  averageCompletionRatio: number;
  totalCompleted: number;
  successRate: number;
}

interface TaskMetrics {
  statusCounts: {
    pending: number;
    overdue: number;
    completed: number;
    rescheduled: number;
    archived: number;
    total: number;
  };
  completionRate: number;
  overdueRate: number;
}

const api = {
  goalPipeline: () => apiClient.get<GoalPipeline>('/analytics/goals/pipeline'),
  phaseStats: () => apiClient.get<PhaseStats>('/analytics/goals/phases'),
  taskMetrics: () => apiClient.get<TaskMetrics>('/analytics/goals/tasks'),
};

export function AnalyticsPage() {
  const pipeline = useQuery({
    queryKey: ['analytics', 'goals', 'pipeline'],
    queryFn: () => api.goalPipeline(),
    staleTime: 5 * 60_000,
  });
  const phases = useQuery({
    queryKey: ['analytics', 'goals', 'phases'],
    queryFn: () => api.phaseStats(),
    staleTime: 5 * 60_000,
  });
  const tasks = useQuery({
    queryKey: ['analytics', 'goals', 'tasks'],
    queryFn: () => api.taskMetrics(),
    staleTime: 5 * 60_000,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Analytics · goals"
        description="Pipeline counts, phase-success rate, and task completion. Deeper BigQuery dashboards in Data Studio."
      />

      {pipeline.data && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="h-4 w-4" /> Goal pipeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <StatCard label="Draft" value={pipeline.data.draft} />
              <StatCard label="Active" value={pipeline.data.active} />
              <StatCard label="Graduated" value={pipeline.data.graduated} />
              <StatCard label="Total" value={pipeline.data.total} />
            </section>
          </CardContent>
        </Card>
      )}
      {pipeline.isError && (
        <ErrorState title="Pipeline failed" description={humanizeError(pipeline.error)} />
      )}
      {pipeline.isLoading && <LoadingState label="Loading pipeline" />}

      {phases.data && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList className="h-4 w-4" /> Phases
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <section className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <StatCard label="Pending" value={phases.data.statusCounts.pending} />
              <StatCard label="Active" value={phases.data.statusCounts.active} />
              <StatCard label="Evaluating" value={phases.data.statusCounts.evaluating} />
              <StatCard label="Completed" value={phases.data.statusCounts.completed} />
              <StatCard label="Total" value={phases.data.statusCounts.total} />
            </section>
            <section className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <StatCard label="Avg completion" value={humanizePercent(phases.data.averageCompletionRatio)} />
              <StatCard label="Success rate" value={humanizePercent(phases.data.successRate)} />
              <StatCard label="Total completed" value={phases.data.totalCompleted} />
            </section>
          </CardContent>
        </Card>
      )}
      {phases.isError && <ErrorState title="Phases failed" description={humanizeError(phases.error)} />}
      {phases.isLoading && <LoadingState label="Loading phases" />}

      {tasks.data && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ListChecks className="h-4 w-4" /> Tasks
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <section className="grid grid-cols-2 gap-3 md:grid-cols-6">
              <StatCard label="Pending" value={tasks.data.statusCounts.pending} />
              <StatCard label="Overdue" value={tasks.data.statusCounts.overdue} tone="warning" />
              <StatCard label="Completed" value={tasks.data.statusCounts.completed} tone="success" />
              <StatCard label="Rescheduled" value={tasks.data.statusCounts.rescheduled} />
              <StatCard label="Archived" value={tasks.data.statusCounts.archived} />
              <StatCard label="Total" value={tasks.data.statusCounts.total} />
            </section>
            <section className="grid grid-cols-2 gap-3 md:grid-cols-2">
              <StatCard label="Completion rate" value={humanizePercent(tasks.data.completionRate)} tone="success" />
              <StatCard label="Overdue rate" value={humanizePercent(tasks.data.overdueRate)} tone="warning" />
            </section>
          </CardContent>
        </Card>
      )}
      {tasks.isError && <ErrorState title="Tasks failed" description={humanizeError(tasks.error)} />}
      {tasks.isLoading && <LoadingState label="Loading tasks" />}
    </div>
  );
}
